import { useCallback, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import stationDataConfigApi from '@/api/config/stationDataConfig'
import useTranslation from '@/hooks/useTranslation'
import type { GridApi } from '@/components/grid/types'
import { commitOpenEditor } from '@/components/grid/commitOpenEditor'
import { normalizeStationData } from './normalize-station-data'
import type { StationRow } from './column-defs-builder'
import {
  isStationComplete,
  missingFieldLabels,
  missingStationFields,
} from './station-completeness'
import { getValidationSummary } from '@/helpers/apiError'
import { getApiErrorMessage, getApiErrorStatus } from '@/helpers/notify'
import { STATION_GROUP_ID } from '@/constant/station'
import {
  fetchStationGeoTags,
  readStationGeoTags,
  resolveGeoTag,
  type StationGeoTag,
} from './station-geo-tags'
import {
  createDecoderPurposeSync,
  needsDecoderPurposeSync,
} from './decoder-purpose-sync'
import {
  createHoldUntil,
  holdCreateSent,
  isUnclearCreateFailure,
  releaseCreateHold,
  settleCreateHold,
  stationWritesInFlight,
} from './station-write-registry'

/** Failed stations named in the toast before it turns into "…". */
const MAX_FAILED_NAMES_SHOWN = 5

interface UseStationSaveParams {
  apiRef: React.MutableRefObject<GridApi<StationRow> | null>
  /** Base rows, without the pending edits laid over them. */
  rowsRef: React.MutableRefObject<StationRow[]>
  submitValidatedRef: React.MutableRefObject<boolean>
  setIsSaving: React.Dispatch<React.SetStateAction<boolean>>
  interfaceId: string | number | null
  editStationAsync: (data: any) => Promise<any>
  /**
   * The controller's own plants and soils, used to check that the ids a row
   * holds still exist before they are sent. See `agronomyIds`.
   */
  plantOptionsData: any[]
  soilOptionsData: any[]
  emitterOptionsData: any[]
  etZoneOptionsData: any[]
  controllerOptionsData: any[]
  onToggleBulkEdit?: () => void
  highlightCell: (rowId: string, field: string, color?: string) => void
  /** Replaces the saved rows' base data once the server has confirmed them. */
  onSaved: (freshRows: StationRow[]) => void
  onNewStationsCreated?: () => void
  /** The whole run has ended — refresh the list once, however it went. */
  onSaveSettled?: () => void
  /**
   * Rows settled so far, for the page's progress readout. `null` when the run
   * ends. Each station is a request of its own, so on a long list this is the
   * difference between waiting and wondering (SCRUM-1010 QC item 2).
   */
  onSaveProgress?: (progress: { done: number; total: number } | null) => void
  onRowCreated?: (tempRow: StationRow) => void | Promise<void>
  /**
   * A write that timed out has since answered. The row was held out of later
   * passes while it was out. A late success has already been applied (an
   * update retired, a create recorded); a late refusal carries the server's
   * message and is NOT retried from here — the bounded retry and the user's
   * next edit decide that, or a slow controller that refuses late would be
   * re-armed for as long as the page is open.
   */
  onLateWriteSettled?: (answer: LateWriteAnswer) => void
  /**
   * One row landed, mid-pass. The autosave writes a batch one request at a
   * time, and reporting only at the end left twenty-five rows sitting amber
   * for the length of the batch — the user cannot tell a slow save from a
   * stuck one. Called the moment each row's request comes back.
   */
  onRowSaved?: (
    rowId: string,
    decoderAddress: string,
    /**
     * The staged values this write sent. A draft's row stays editable until
     * the list re-reads; anything typed into it since differs from this, and
     * that is what the caller carries over to the new station.
     */
    written?: Record<string, unknown>,
  ) => void
  /**
   * Decoder addresses already written this sitting, owned by the caller. A
   * station created a moment ago is not in the list until it refetches, so
   * this is the other half of "does this decoder already have one?".
   */
  writtenAddressesRef?: React.MutableRefObject<Set<string>>
  /**
   * When the station list on screen was requested (ms). A create whose
   * outcome is unknown is decided only by a read that started after it; see
   * `station-write-registry.ts`. Absent, nothing held is ever released here.
   */
  listReadStartedAtRef?: React.MutableRefObject<number>
  /**
   * Whether the inputs a payload is built from are in: the option lists (ids
   * are checked against them) and, in the wizard, a fresh read of the
   * decoders. Nothing is written while this is false. Absent means ready.
   */
  writeReady?: () => boolean
}

/** How long the bulk save waits for the option lists before giving up. */
const WRITE_READY_WAIT_MS = 30_000

/**
 * The fields a station cannot be written without, as a list rather than a
 * verdict — so the save can paint them red and the autosave can simply pass
 * over a row that is not ready yet. See `station-completeness.ts`.
 */
/**
 * How many rows one autosave pass writes. The creates are sequential, so this
 * is also how long a pass can hold the "saving" state: 25 × 120ms ≈ 3s.
 */
/** `stationType` 1 — a station that does not irrigate. */
const NON_IRRIGATION_TYPE = 1

/**
 * The irrigation figures, as a station of this type should report them.
 *
 * A station that does not irrigate has no water requirement and no flow — it
 * has nothing to say about them, which is null. Zero is an answer: it says
 * "none of it", and it is the value an irrigating station gives when it is
 * genuinely set to nothing.
 *
 * `defaultRunTime` is the exception, and the server is what decides that: it
 * answers a null one with "The DefaultRunTime field is required".
 */
export const irrigationFigures = (station: any) => {
  const isNonIrrigation = Number(station.stationType) === NON_IRRIGATION_TYPE
  const figure = (value: unknown) =>
    isNonIrrigation ? null : Number(value) || 0

  return {
    surfaceAccumulation: figure(station.surfaceAccumulation),
    waterRequired: figure(station.waterRequired),
    plantAvailWater: figure(station.plantAvailWater),
    precipitationRate: figure(station.precipitationRate),
    expectedFlow: figure(station.expectedFlow),
    landscapeFactor: figure(station.landscapeFactor),
    rtDayOverride: figure(station.rtDayOverride),
    // The one figure the server will not take as null: `DefaultRunTime` is a
    // required field, and a station that does not irrigate says so with a zero
    // run time rather than by omitting it. The rest it accepts as null.
    defaultRunTime: station.defaultRunTime || '00:00:00',
  }
}

const AUTOSAVE_BATCH_SIZE = 25
/**
 * Breathing room between two writes.
 *
 * Zero, with a yield: each station write goes through to the SRC controller
 * API, which takes them one at a time and has no bulk endpoint — so the pass
 * is already as serialised as it can be, and the next request is not sent
 * until the last has answered. The 500s this gap once guarded against came
 * from requests arriving *together*, which cannot happen here. At 120ms it was
 * a full minute of deliberate waiting on a five-hundred-station controller,
 * added to a total the server already dominates.
 *
 * The `setTimeout` stays: it hands the thread back between rows so the grid
 * can paint the row that just turned green.
 */
const AUTOSAVE_REQUEST_GAP_MS = 0

/**
 * How long one write may take before the pass gives up on it.
 *
 * A request that never settles used to wedge the whole autosave: the pass
 * never returned, so the in-flight guard turned every later pass into a no-op
 * and the toolbar sat on "Saving completed rows…" forever, writing nothing.
 * A row that times out is reported like any other failure and tried again on
 * the next pass.
 */
const AUTOSAVE_REQUEST_TIMEOUT_MS = 20_000

/** Marks the giving-up so a caller can tell it from a refusal. */
export class WriteTimeoutError extends Error {}


const withTimeout = async <T,>(
  work: Promise<T>,
  timeoutMessage: string,
): Promise<T> => {
  let timer: number | undefined
  try {
    return await Promise.race([
      work,
      new Promise<never>((_, reject) => {
        timer = window.setTimeout(
          () => reject(new WriteTimeoutError(timeoutMessage)),
          AUTOSAVE_REQUEST_TIMEOUT_MS,
        )
      }),
    ])
  } finally {
    if (timer !== undefined) window.clearTimeout(timer)
  }
}

// DECISION: the decoder is put back only on a 4xx, not on any failed station write, because a timeout, network error or 5xx may still have written the station and undoing the decoder then leaves the two on different types.
const isServerRefusal = (error: unknown) => {
  const status = getApiErrorStatus(error)
  if (status === undefined || status === 408 || status === 429) return false
  return status >= 400 && status < 500
}

/** What a write that outlived its pass finally answered. */
export interface LateWriteAnswer {
  kind: 'create' | 'update'
  rowId: string
  ok: boolean
  message?: string
}

type LateOutcome<T> = { ok: true; value: T } | { ok: false; error: unknown }

/**
 * Sends one write and waits for it at most the pass's timeout, keeping it in
 * `inFlight` under `key` until it actually answers.
 *
 * SCRUM-1032 round 7 follow-up — the timeout ends the wait, not the request.
 * On a slow controller (60–113 s a write on D2) later passes sent the same
 * write again while earlier ones were still running: up to five PUTs at once
 * for one row, and a create sent twice is two stations on one decoder. A key
 * in `inFlight` is skipped by every pass until its request answers; the late
 * answer goes to `onLate`.
 */
export const sendTrackedWrite = async <T,>(
  inFlight: Map<string, Promise<unknown>>,
  key: string,
  request: Promise<T>,
  timeoutMessage: string,
  onLate: (outcome: LateOutcome<T>) => void,
): Promise<T> => {
  inFlight.set(key, request)
  let stillOut = false
  try {
    return await withTimeout(request, timeoutMessage)
  } catch (caught) {
    if (caught instanceof WriteTimeoutError) {
      stillOut = true
      request
        .then(
          (value): LateOutcome<T> => ({ ok: true, value }),
          (error): LateOutcome<T> => ({ ok: false, error }),
        )
        .then((outcome) => {
          if (inFlight.get(key) === request) inFlight.delete(key)
          onLate(outcome)
        })
        .catch((error) => console.error('Late station write handling failed:', error))
    }
    throw caught
  } finally {
    if (!stillOut && inFlight.get(key) === request) inFlight.delete(key)
  }
}

/** The ET zone, checked like the agronomy ids; see `agronomyIds`. */
const knownEtZoneId = (station: any, etZoneOptionsData: any[]) => {
  if (!station.etZoneId) return null
  if (!etZoneOptionsData?.length) return station.etZoneId
  return etZoneOptionsData.some((zone) => zone.id === station.etZoneId)
    ? station.etZoneId
    : null
}

// DECISION: the row's own decoderId wins over the address-index match and is sent even when the controller options do not list it, not dropped by an existence check, because the options hold only the picker's decoders (a Non-Irrigation decoder is often absent) and dropping its id recreates the 0-0 unlinked station; two control-hub sub-controllers can also share an address-index.
const matchController = (station: any, controllerOptionsData: any[]) => {
  const byId = station.decoderId
    ? controllerOptionsData.find((controller) => controller.id === station.decoderId)
    : undefined
  if (byId) return byId
  if (!station.decoderAddress) return null
  const candidates = controllerOptionsData.filter(
    (controller) =>
      `${controller.address}-${controller.index}` === station.decoderAddress &&
      (station.twiId == null || controller.twiId === station.twiId),
  )
  return candidates.length === 1 ? candidates[0] : null
}

export const requiredFieldsMessage = (lead: string, stations: any[]) => {
  const fields = [...new Set(stations.flatMap((station) => missingFieldLabels(station)))]
  return fields.length ? `${lead}: ${fields.join(', ')}` : lead
}

const validateStation = (
  station: any,
  highlightCell: (rowId: string, field: string, color?: string) => void,
): boolean => {
  const missing = missingStationFields(station)
  missing.forEach((field) => highlightCell(String(station.rowId), field, '#ffebee'))
  return missing.length > 0
}


/**
 * Plant, soil and emitter, as ids that stand for something.
 *
 * The row's own ids where it has them, and null where it does not — never
 * invented, never zeroed. The three cases this has to serve:
 *
 * - An irrigating station always has all three; the save refuses the row
 *   otherwise. They go up as they are, checked against the options the
 *   controller currently offers so an id whose plant has since been deleted is
 *   sent as null rather than as a key the server would reject.
 * - A non-irrigation station that already exists carries them anyway — the
 *   importer wrote real ids, and a PUT that keeps them is what the /config
 *   station drawer sends and what the server accepts. Dropping them would ask
 *   the server to unset a field the user never touched.
 * - A non-irrigation station being created has none, and null is what says so.
 *   The old create sent `plantId: 0`, a foreign key matching no plant, and the
 *   row was refused as a constraint violation. Standing a real id in its place
 *   would satisfy the constraint by claiming something untrue about a station
 *   that does not irrigate.
 */
const agronomyIds = (
  station: any,
  ctx: {
    plantOptionsData: any[]
    soilOptionsData: any[]
    emitterOptionsData: any[]
  },
): {
  plantId: number | null
  soilId: number | null
  emitterId: number | null
} => {
  const known = (options: any[], id: any) => {
    if (id == null || id === '') return null
    // An empty list is not an answer: it is what a list that failed to load,
    // or has not loaded yet, looks like. The row keeps the id it has rather
    // than having it nulled on the server (SCRUM-1032 review).
    if (!options?.length) return Number(id) || null
    return options.some((option) => Number(option.id) === Number(id))
      ? Number(id)
      : null
  }

  return {
    plantId: known(ctx.plantOptionsData, station.plantId),
    soilId: known(ctx.soilOptionsData, station.soilId),
    emitterId: known(ctx.emitterOptionsData, station.emitterId),
  }
}

/**
 * The PUT body for an existing station, as `handleSave` sends it.
 *
 * The station list this grid is built from does not return the geo pin, so the
 * caller reads the current pins from the map endpoint and passes them in as
 * `geoById`. Without them the row's own value — null unless the GPS picker set
 * one — went up and the PUT wiped the pin (SCRUM-1144). The floor comes from
 * there too: the row's goes stale when a station is pinned during edit mode.
 */
export const buildUpdatePayload = (
  station: any,
  ctx: {
    interfaceId: string | number | null
    controllerOptionsData: any[]
    plantOptionsData: any[]
    soilOptionsData: any[]
    emitterOptionsData: any[]
    etZoneOptionsData: any[]
    geoById?: Map<number, StationGeoTag>
  },
) => {
  const isNonIrrigation = Number(station.stationType) === 1

  // Derived from decoderAddress, exactly as the create payload does and for
  // the same reason: `station.address`/`index` can be stale or absent, and
  // `Number(undefined) || 0` writes the decoder back as "0-0" — which is what
  // happened to three stations here. The caller refuses to send a zero.
  const decoderParts =
    typeof station.decoderAddress === 'string' &&
    station.decoderAddress.includes('-')
      ? station.decoderAddress.split('-')
      : null
  const decoderAddressNum = decoderParts
    ? Number(decoderParts[0]) || 0
    : Number(station.address) || 0
  const decoderIndexNum = decoderParts
    ? Number(decoderParts[1]) || 0
    : Number(station.index) || 0

  // Resolved, not passed through. The list rows do not always carry a twiId,
  // and sending 0 is what the API answers with "Controller does not exist".
  // On a control hub two sub-controllers can share an address-index, so a
  // staged twiId wins and the address is only the fallback.
  const matchedController = matchController(station, ctx.controllerOptionsData)
  const twiId = station.twiId ?? matchedController?.twiId ?? 0

  // An id the options no longer hold would be rejected; the ET zone is optional.
  const validEtZoneId = knownEtZoneId(station, ctx.etZoneOptionsData)

  // Present only for an irrigating station; absent from the body otherwise.
  const agronomy = agronomyIds(station, ctx)

  return {
    id: Number(station.id),
    interfaceId: Number(ctx.interfaceId),
    priority: isNonIrrigation ? 0 : Number(station.priority) || 0,
    pictureUrl: station.pictureUrl || '',
    pictureFileName: station.pictureFileName || '',
    ...resolveGeoTag(station, ctx.geoById),
    ...irrigationFigures(station),
    description: station.description,
    decoderId: station.decoderId || matchedController?.id || undefined,
    index: decoderIndexNum,
    address: decoderAddressNum,
    ...agronomy,
    pipeId:
      Number(station.stationType) !== NON_IRRIGATION_TYPE
        ? station.pipe?.id || station.pipeId || 0
        : null,
    etZoneId: validEtZoneId,
    stationGroupId: STATION_GROUP_ID,
    subAreaId: station.subAreaId || null,
    twiId,
    Type: Number(station.stationType) || 2,
  }
}

// Resolves controller/emitter/etZone references and shapes the POST body for a
// newly-created station (matches the CreateStation contract).
export const buildCreatePayload = (
  station: any,
  ctx: {
    interfaceId: string | number | null
    controllerOptionsData: any[]
    plantOptionsData: any[]
    soilOptionsData: any[]
    emitterOptionsData: any[]
    etZoneOptionsData: any[]
  },
) => {
  // Prefer the twiId the picker staged. On a control hub two sub-controllers
  // can hold the same address-index, so resolving by address alone returns
  // whichever the list happens to hold first and can file the station under
  // the wrong controller. The address lookup stays as the fallback for rows
  // that were never edited through the picker.
  const matchedController = matchController(station, ctx.controllerOptionsData)
  const twiId = station.twiId ?? matchedController?.twiId ?? 0

  // decoderAddress ("<address>-<index>") is the source of truth for the decoder.
  // Derive address/index from it — station.address/index can be stale/0 at save
  // time, which would persist the decoder as "0-0" on the server.
  const decoderParts =
    typeof station.decoderAddress === 'string' &&
    station.decoderAddress.includes('-')
      ? station.decoderAddress.split('-')
      : null
  const decoderAddressNum = decoderParts
    ? Number(decoderParts[0]) || 0
    : Number(station.address) || 0
  const decoderIndexNum = decoderParts
    ? Number(decoderParts[1]) || 0
    : Number(station.index) || 0

  // Present only for an irrigating station; absent from the body otherwise.
  const agronomy = agronomyIds(station, ctx)

  const validEtZoneId = knownEtZoneId(station, ctx.etZoneOptionsData)

  // A non-irrigation station carries no irrigation data. Its figures go as
  // null (see `irrigationFigures`); priority and pipe are settled here, since
  // the create refused a non-irrigation row that went up with the drafts'
  // default priority of 1.
  const isNonIrrigation = Number(station.stationType) === NON_IRRIGATION_TYPE

  return {
    interfaceId: Number(ctx.interfaceId),
    description: station.description || '',
    pictureUrl: station.pictureUrl || '',
    pictureFileName: station.pictureFileName || '',
    priority: isNonIrrigation ? 0 : Number(station.priority) || 1,
    grsCoordinatedLatitude: station.grsCoordinatedLatitude ?? null,
    grsCoordinatedLongitude: station.grsCoordinatedLongitude ?? null,
    floorId: station.floorId ?? null,
    ...irrigationFigures(station),
    decoderId: station.decoderId || matchedController?.id || undefined,
    index: decoderIndexNum,
    address: decoderAddressNum,
    ...agronomy,
    pipeId: isNonIrrigation
      ? undefined
      : station.pipe?.id || station.pipeId || 0,
    etZoneId: validEtZoneId,
    stationGroupId: STATION_GROUP_ID,
    subAreaId: Number(station.subAreaId) || 0,
    twiId,
    Type: Number(station.stationType) || 2,
  }
}

export const useStationSave = ({
  apiRef,
  rowsRef,
  submitValidatedRef,
  setIsSaving,
  interfaceId,
  editStationAsync,
  plantOptionsData,
  soilOptionsData,
  emitterOptionsData,
  etZoneOptionsData,
  controllerOptionsData,
  onToggleBulkEdit,
  highlightCell,
  onSaved,
  onNewStationsCreated,
  onSaveSettled,
  onSaveProgress,
  onRowCreated,
  onRowSaved,
  onLateWriteSettled,
  writtenAddressesRef: callerWrittenAddressesRef,
  listReadStartedAtRef,
  writeReady,
}: UseStationSaveParams) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const savingRowIdsRef = useRef<Set<string>>(new Set())
  /**
   * Writes still out after their pass gave up on them, keyed
   * `update:<rowId>` / `create:<address>`. See `sendTrackedWrite`.
   */
  const writesInFlightRef = useMemo(
    // Per controller and outside this hook: Back, Exit and the rail unmount
    // the grid, and a write still out must keep blocking its twin when the
    // step comes back (SCRUM-1032 review).
    () => ({ current: stationWritesInFlight(interfaceId) }),
    [interfaceId],
  )
  const listReadStartedAtRefLatest = useRef(listReadStartedAtRef)
  listReadStartedAtRefLatest.current = listReadStartedAtRef
  /** 0 when the caller cannot say: a held create then stays held. */
  const listReadStartedAt = useCallback(
    () => listReadStartedAtRefLatest.current?.current ?? 0,
    [],
  )
  const writeReadyRef = useRef(writeReady)
  writeReadyRef.current = writeReady
  const isWriteReady = useCallback(() => writeReadyRef.current?.() ?? true, [])
  const waitForWriteReady = useCallback(async () => {
    const deadline = Date.now() + WRITE_READY_WAIT_MS
    while (!isWriteReady()) {
      if (Date.now() >= deadline) return false
      await new Promise((resolve) => setTimeout(resolve, 200))
    }
    return true
  }, [isWriteReady])
  /**
   * Addresses written this sitting. The caller's set when it passes one (it
   * also retires drafts by it); otherwise this hook's own, so a create that
   * landed late is never sent again either way.
   */
  const ownWrittenAddressesRef = useRef<Set<string>>(new Set())
  const writtenAddressesRef = callerWrittenAddressesRef ?? ownWrittenAddressesRef
  const onLateWriteSettledRef = useRef(onLateWriteSettled)
  onLateWriteSettledRef.current = onLateWriteSettled

  /** SCRUM-1145 — one per pass; see `decoder-purpose-sync.ts`. */
  const newDecoderPurposeSync = useCallback(
    () =>
      createDecoderPurposeSync({
        interfaceId,
        queryClient,
        notFoundMessage: t('stationsDecoderSyncNotFound'),
        unknownPurposeMessage: t('stationsDecoderSyncUnknownPurpose'),
        writeFailedMessage: t('stationsDecoderSyncWriteFailed'),
      }),
    [interfaceId, queryClient, t],
  )

  /**
   * After a create: the station is written whatever happens here, so a decoder
   * that could not follow is reported rather than failing the row. Left alone,
   * the wizard puts the station back on its decoder's type (SCRUM-1157), which
   * keeps the two in agreement either way.
   */
  const syncDecoderAfterCreate = useCallback(
    async (
      sync: ReturnType<typeof createDecoderPurposeSync>,
      station: any,
      failedNames: string[],
    ) => {
      if (!needsDecoderPurposeSync(station)) return
      try {
        await sync.apply(station)
      } catch (error) {
        console.error('Station decoder purpose sync failed:', error)
        failedNames.push(
          String(station.description || station.decoderAddress || station.id),
        )
      }
    },
    [],
  )

  const reportDecoderSyncFailures = useCallback(
    (failedNames: string[]) => {
      if (failedNames.length === 0) return
      const shown = failedNames.slice(0, MAX_FAILED_NAMES_SHOWN)
      const names =
        failedNames.length > shown.length ? `${shown.join(', ')}, …` : shown.join(', ')
      toast.error(`${t('stationsDecoderSyncFailed')} — ${names}`)
    },
    [t],
  )

  /**
   * The change set IS the grid's pending-edit buffer: every row it names has an
   * unsaved edit, and laying that edit over the base row gives the station to
   * send. No field-by-field diff against a snapshot, and no flags on the rows.
   */
  const collectChangedStations = useCallback((): any[] => {
    const pendingEdits = apiRef.current?.getPendingEdits() ?? new Map()
    const rowsById = new Map(rowsRef.current.map((row) => [String(row.id), row]))
    const changed: any[] = []

    /**
     * SCRUM-1032 QC item 15c — a decoder gets one station, not two.
     *
     * `saveCompleteRows` has always taken already-stationed decoders out of its
     * batch; this path never did, and it is the one the wizard calls on Next.
     * A draft is retired through React state, which does not settle until the
     * run yields — so a save starting right after the autosave still saw drafts
     * whose stations had just been written, and created them a second time.
     * That is the "72 sharing a decoder" in the QC report.
     *
     * The serialised gate stops the two runs overlapping; this stops the second
     * one re-creating what the first already wrote.
     */
    const alreadyStationed = new Set(
      rowsRef.current
        .filter((row) => Number(row.id) > 0)
        .map((row) => String((row as any).decoderAddress ?? ''))
        .filter((address) => address && address !== '-'),
    )

    rowsRef.current.forEach((row) => {
      // Temp rows (negative id) are creates, whether or not they were edited.
      if (typeof row.id === 'number' && row.id < 0) {
        if (row.__isEmptyPlaceholder && !row.decoderAddress) return
        const edit = pendingEdits.get(String(row.id)) ?? {}
        const candidate = { ...row, ...edit }
        const address = String((candidate as any).decoderAddress ?? '')
        if (address && alreadyStationed.has(address)) return
        changed.push({ ...candidate, rowId: String(row.id), __isNew: true })
      }
    })

    pendingEdits.forEach((edit, rowId) => {
      const base = rowsById.get(rowId)
      if (!base) return
      if (typeof base.id === 'number' && base.id < 0) return
      changed.push({ ...base, ...edit, rowId })
    })

    return changed
  }, [apiRef, rowsRef])

  const handleSave = useCallback(async () => {
    submitValidatedRef.current = true

    const api = apiRef.current
    if (!api) return

    // Flush any cell still being edited first: clicking Save with an editor
    // open would otherwise save the value from before the user typed.
    commitOpenEditor(api)

    const changedStations = collectChangedStations()

    if (changedStations.length === 0) {
      api.clearPendingEdits()
      onToggleBulkEdit?.()
      return
    }

    const hasValidationError = changedStations
      .map((station) => validateStation(station, highlightCell))
      .some(Boolean)

    if (hasValidationError) {
      toast.error(requiredFieldsMessage(t('PleaseFillInRequiredFields'), changedStations))
      return
    }

    // The option lists decide plant, soil, emitter and controller ids; a save
    // before they are in would send them blank (SCRUM-1032 review).
    if (!(await waitForWriteReady())) {
      toast.error(t('stationsToastLoadOptionsFailed'))
      return
    }

    setIsSaving(true)
    let successCount = 0
    let errorCount = 0
    let createdCount = 0
    const apiErrorMessages: string[] = []
    /**
     * The rows this pass actually wrote. Only these are retired from the staged
     * buffer at the end: a row the user edited *during* the save was never in
     * `changedStations`, and clearing the buffer wholesale threw it away with
     * nothing said (SCRUM-1010 QC item 4).
     */
    const writtenRowIds: string[] = []
    /** Named in the toast, so a failure says which station, not just how many. */
    const failedNames: string[] = []
    /** Rows settled — written or failed — for the progress readout. */
    let settled = 0
    const reportProgress = () =>
      onSaveProgress?.({ done: settled, total: changedStations.length })
    reportProgress()

    // The grid rows carry no geo pin; see `fetchStationGeoTags`.
    const geoById = await fetchStationGeoTags(interfaceId)
    const decoderSync = newDecoderPurposeSync()
    /** Created stations whose decoder could not take their Type. */
    const decoderSyncFailed: string[] = []

    try {
      for (const station of changedStations) {
        try {
          // A write the autosave sent is still out for this row: sending it
          // again here would overlap it on the controller. Left staged and
          // named as not saved, for the user to try once it has answered.
          const key = station.__isNew
            ? `create:${station.decoderAddress ?? ''}`
            : `update:${station.rowId ?? station.id}`
          if (writesInFlightRef.current.has(key)) {
            throw new Error(t('stationsSaveTimeout'))
          }
          if (station.__isNew) {
            const address = String(station.decoderAddress ?? '')
            // A create sent earlier — by this grid before Back, or before a
            // reload — whose outcome is not known yet. Sending it again is how
            // a decoder gets two stations; left staged, named as not saved.
            if (address && createHoldUntil(interfaceId, address) !== undefined) {
              const until = createHoldUntil(interfaceId, address) as number
              if (!(listReadStartedAt() > until)) {
                throw new Error(t('stationsSaveTimeout'))
              }
              releaseCreateHold(interfaceId, address)
            }
            const sentAt = Date.now()
            holdCreateSent(interfaceId, address, sentAt)
            try {
              await stationDataConfigApi.create(
                buildCreatePayload(station, {
                  interfaceId,
                  controllerOptionsData,
                  plantOptionsData,
                  soilOptionsData,
                  emitterOptionsData,
                  etZoneOptionsData,
                }) as any,
              )
            } catch (caught) {
              settleCreateHold(
                interfaceId,
                address,
                isUnclearCreateFailure(caught) ? 'unclear' : 'refused',
                sentAt,
              )
              throw caught
            }
            settleCreateHold(interfaceId, address, 'created', sentAt)
            await syncDecoderAfterCreate(decoderSync, station, decoderSyncFailed)
            createdCount++
            writtenRowIds.push(String(station.rowId ?? station.id))
            settled++
            reportProgress()
            continue
          }

          // One body for every update, so this path gets the same guards as
          // the autosave: the decoder derived from its address (never "0-0"),
          // and plant/soil/emitter checked rather than sent as `|| 0`.
          const payload = buildUpdatePayload(station, {
            interfaceId,
            controllerOptionsData,
            plantOptionsData,
            soilOptionsData,
            emitterOptionsData,
            etZoneOptionsData,
            geoById,
          })
          if (!payload.address) {
            throw new Error(t('stationsSaveDecoderReadFailed'))
          }

          // SCRUM-1145 — the decoder before the station. If the decoder cannot
          // be written the station is not either, and the row stays staged
          // with the reason; if the station is refused, the decoder is put
          // back. Either way the two are not left on different types.
          const writeStation = () => editStationAsync(payload)
          if (needsDecoderPurposeSync(station)) {
            const purpose = await decoderSync.withStation(
              station,
              writeStation,
              isServerRefusal,
            )
            if (purpose) station.decoderType = purpose
          } else {
            await writeStation()
          }
          successCount++
          writtenRowIds.push(String(station.rowId ?? station.id))
          settled++
          reportProgress()
        } catch (error: any) {
          errorCount++
          failedNames.push(String(station.description || station.decoderAddress || station.id))
          settled++
          reportProgress()
          const message = getValidationSummary(error, '')
          if (message) apiErrorMessages.push(message)
        }
      }

      if (successCount > 0) {
        toast.success(t('stationsToastUpdateSuccess', { count: successCount }))
      }
      if (createdCount > 0) {
        toast.success(t('stationsToastCreateSuccess', { count: createdCount }))
      }
      reportDecoderSyncFailures(decoderSyncFailed)
      if (errorCount > 0) {
        // The rows that failed stay staged and amber so the user can retry just
        // those; the ones that landed are retired below. Named rather than
        // counted — a bare count left QC with nothing to go on.
        writtenRowIds.forEach((rowId) => api.removePendingEdit(rowId))
        const shown = failedNames.slice(0, MAX_FAILED_NAMES_SHOWN)
        const names =
          failedNames.length > shown.length ? `${shown.join(', ')}, …` : shown.join(', ')
        toast.error(
          t('stationsToastSaveFailedCount', { count: errorCount }) +
            (names ? ` — ${names}` : '') +
            (apiErrorMessages[0] ? `: ${apiErrorMessages[0]}` : ''),
        )
        return
      }
      if (createdCount > 0) onNewStationsCreated?.()

      // Pull the saved rows back so the base data matches the server (a save can
      // normalise values), then drop the buffer: the edits are no longer pending.
      const savedIds = new Set(changedStations.map((station) => Number(station.id)))
      let freshRows: StationRow[] = []
      try {
        const { data: freshData } = await stationDataConfigApi.getAll({
          interfaceId: Number(interfaceId),
          PageSize: 0,
          PageIndex: 1,
        })
        if (freshData?.data) {
          freshRows = normalizeStationData(
            freshData.data.filter((item: any) => savedIds.has(Number(item.id))),
            t,
          )
        }
      } catch {
        // Falls back to the rows already on screen.
      }

      // Per row, not wholesale: anything staged while the save was running has
      // not been written yet and must survive (SCRUM-1010 QC item 4).
      //
      // What is left is worked out before the removals, not read back after
      // them: `removePendingEdit` goes through React state, so the buffer this
      // still sees is the one from before the pass.
      const written = new Set(writtenRowIds)
      const leftover = [...api.getPendingEdits().keys()].filter(
        (rowId) => !written.has(rowId),
      )
      writtenRowIds.forEach((rowId) => api.removePendingEdit(rowId))
      onSaved(freshRows)
      // Rows staged mid-save are unsaved work; leaving bulk edit would hide
      // them behind a mode switch.
      if (leftover.length === 0) onToggleBulkEdit?.()
      else toast(t('stationsToastEditedWhileSaving', { count: leftover.length }))
    } catch (error) {
      toast.error(t('stationsToastSaveFailed'))
    } finally {
      setIsSaving(false)
      onSaveProgress?.(null)
      onSaveSettled?.()
    }
  }, [
    apiRef,
    collectChangedStations,
    controllerOptionsData,
    editStationAsync,
    plantOptionsData,
    soilOptionsData,
    emitterOptionsData,
    etZoneOptionsData,
    highlightCell,
    interfaceId,
    newDecoderPurposeSync,
    onNewStationsCreated,
    onSaveProgress,
    onSaveSettled,
    onSaved,
    onToggleBulkEdit,
    reportDecoderSyncFailures,
    setIsSaving,
    submitValidatedRef,
    syncDecoderAfterCreate,
    t,
    listReadStartedAt,
    waitForWriteReady,
    writesInFlightRef,
  ])

  const handleSaveRow = useCallback(
    async (row: StationRow) => {
      submitValidatedRef.current = true

      const api = apiRef.current
      if (!api || !row) return

      commitOpenEditor(api)

      const rowId = String(row.id)
      const edit = api.getPendingEdits().get(rowId) ?? {}
      const station: any = { ...row, ...edit, rowId }

      if (station.__isEmptyPlaceholder && !station.decoderAddress) return

      if (validateStation(station, highlightCell)) {
        toast.error(requiredFieldsMessage(t('PleaseFillInRequiredFields'), [station]))
        return
      }

      if (savingRowIdsRef.current.has(rowId)) return

      const address = String(station.decoderAddress ?? '')
      const holdUntil = address ? createHoldUntil(interfaceId, address) : undefined
      if (
        (address && writesInFlightRef.current.has(`create:${address}`)) ||
        (holdUntil !== undefined && !(listReadStartedAt() > holdUntil))
      ) {
        toast.error(t('stationsSaveTimeout'))
        return
      }
      if (holdUntil !== undefined) releaseCreateHold(interfaceId, address)
      if (!(await waitForWriteReady())) {
        toast.error(t('stationsToastLoadOptionsFailed'))
        return
      }
      if (savingRowIdsRef.current.has(rowId)) return
      savingRowIdsRef.current.add(rowId)

      setIsSaving(true)
      const sentAt = Date.now()
      try {
        holdCreateSent(interfaceId, address, sentAt)
        try {
          await stationDataConfigApi.create(
            buildCreatePayload(station, {
              interfaceId,
              controllerOptionsData,
              plantOptionsData,
              soilOptionsData,
              emitterOptionsData,
              etZoneOptionsData,
            }) as any,
          )
        } catch (caught) {
          settleCreateHold(
            interfaceId,
            address,
            isUnclearCreateFailure(caught) ? 'unclear' : 'refused',
            sentAt,
          )
          throw caught
        }
        settleCreateHold(interfaceId, address, 'created', sentAt)
        const decoderSyncFailed: string[] = []
        await syncDecoderAfterCreate(
          newDecoderPurposeSync(),
          station,
          decoderSyncFailed,
        )
        toast.success(t('stationsToastStationCreated'))
        reportDecoderSyncFailures(decoderSyncFailed)
        api.removePendingEdit(rowId)
        if (onRowCreated) await onRowCreated(row)
      } catch (error: any) {
        const message = getValidationSummary(error, '')
        toast.error(
          t('stationsToastCreateStationFailed') + (message ? `: ${message}` : ''),
        )
      } finally {
        savingRowIdsRef.current.delete(rowId)
        setIsSaving(false)
      }
    },
    [
      apiRef,
      controllerOptionsData,
      plantOptionsData,
      soilOptionsData,
      emitterOptionsData,
      etZoneOptionsData,
      highlightCell,
      interfaceId,
      newDecoderPurposeSync,
      onRowCreated,
      reportDecoderSyncFailures,
      setIsSaving,
      submitValidatedRef,
      syncDecoderAfterCreate,
      t,
      listReadStartedAt,
      waitForWriteReady,
      writesInFlightRef,
    ],
  )

  /**
   * SCRUM-1032 — writes the rows that are ready, and only those.
   *
   * The guided setup's station list can be a thousand rows built from the
   * decoders, which no one fills in one sitting. This runs on a pause and
   * creates every staged row that already carries its required fields, leaving
   * the rest alone — silently. That silence is the point: `handleSave` refuses
   * the whole batch and raises a toast naming the missing fields, which on a
   * timer would fire every few seconds at someone who is still typing.
   *
   * Sequential and capped per pass: these endpoints answer 500 under concurrent
   * writes, and filling a column down four hundred rows would otherwise queue
   * four hundred creates into one burst. The next pause picks up where this one
   * stopped.
   */

  const saveCompleteRows = useCallback(
    async (
      limit = AUTOSAVE_BATCH_SIZE,
      /**
       * Called once, just before the first request of a pass that has
       * something to write — not on a pass that finds nothing ready, so the
       * caller can lock the grid for real writes only.
       */
      onWriteStart?: () => void,
    ): Promise<{
      created: number
      failed: number
      updated: number
      createdAddresses: string[]
      createdRowIds: string[]
      updatedIds: string[]
      failures: { rowId: string; message: string }[]
      /** Saved stations whose edit emptied a required field: not written. */
      refused: { rowId: string; fields: string[] }[]
      /**
       * Writes still out from earlier passes. Not failures yet, but not
       * saved either: the wizard must not treat the step as written while
       * one could still be refused.
       */
      stillOut: number
      /**
       * Complete drafts not sent because an earlier create on their decoder
       * has no known outcome yet (sent before Back or a reload, or answered
       * with a 5xx). Not saved, so counted against Next like `stillOut`.
       */
      held: number
      /**
       * The earliest time a list read must start after to decide a held
       * create, so the caller can re-read then. Null when nothing is held.
       */
      heldUntil: number | null
      error: string | null
    }> => {
      const api = apiRef.current
      // Not until the option lists are in: a payload built without them
      // sends plant, soil, emitter and controller ids blank.
      if (!api || !isWriteReady())
        return {
          created: 0,
          failed: 0,
          updated: 0,
          createdAddresses: [],
          createdRowIds: [],
          updatedIds: [],
          failures: [],
          refused: [],
          error: null,
          stillOut: writesInFlightRef.current.size,
          held: 0,
          heldUntil: null,
        }

      const pendingEdits = api.getPendingEdits()

      /**
       * Edits to stations that already exist. These are updates, not creates —
       * and until now the pass only looked at the temp rows, so changing a
       * value on a saved station staged an edit that autosave never wrote. It
       * sat amber until the wizard's Finish, which is not what "autosave"
       * means to the person watching it.
       */
      const editedSaved = rowsRef.current
        .filter((row) => Number(row.id) > 0 && pendingEdits.has(String(row.id)))
        .map((row) => ({
          ...row,
          ...(pendingEdits.get(String(row.id)) ?? {}),
          rowId: String(row.id),
        }))

      // A saved station has to stay valid: an edit that empties a required
      // field is not written as a blank. It is reported back, so the row can
      // say what it needs instead of sitting amber with no explanation.
      const refused = editedSaved
        .filter((station: any) => !isStationComplete(station))
        .map((station: any) => ({
          rowId: String(station.rowId),
          fields: missingFieldLabels(station),
        }))
      // A row with a write still out is left out before the batch is cut, so
      // it neither takes a batch slot nor makes an empty pass lock the grid.
      const editedSavedReady = editedSaved
        .filter((station: any) => isStationComplete(station))
        .filter(
          (station: any) =>
            !writesInFlightRef.current.has(`update:${station.rowId}`),
        )
        .slice(0, limit)

      /**
       * The decoders that already have a station: the ones on the server, and
       * the ones written earlier in this run.
       *
       * Taken out of the running before the batch is sliced, not after. The
       * caller retires a written draft through React state, which does not
       * settle until the run yields — so a second pass still saw the rows the
       * first had just written, filled its twenty-five with them, skipped all
       * twenty-five as already-written, and reported nothing written. That read
       * as "one batch, then it stopped", which is exactly what it was.
       */
      const alreadyStationed = new Set([
        ...rowsRef.current
          .filter((row) => Number(row.id) > 0)
          .map((row) => String((row as any).decoderAddress ?? ''))
          .filter((address) => address && address !== '-'),
        ...writtenAddressesRef.current,
      ])

      let held = 0
      let heldUntil: number | null = null
      // A station the list now shows settles its decoder's hold.
      const readAt = listReadStartedAt()
      alreadyStationed.forEach((address) => {
        const until = createHoldUntil(interfaceId, address)
        if (until !== undefined && readAt > until)
          releaseCreateHold(interfaceId, address)
      })

      const ready = rowsRef.current
        .filter((row) => typeof row.id === 'number' && (row.id as number) < 0)
        .map((row) => ({
          ...row,
          ...(pendingEdits.get(String(row.id)) ?? {}),
          rowId: String(row.id),
        }))
        .filter((station: any) => !station.__isEmptyPlaceholder || station.decoderAddress)
        .filter(
          (station: any) =>
            !alreadyStationed.has(String(station.decoderAddress ?? '')),
        )
        .filter((station: any) => isStationComplete(station))
        .filter(
          (station: any) =>
            !writesInFlightRef.current.has(
              `create:${station.decoderAddress ?? ''}`,
            ),
        )
        .filter((station: any) => {
          // A create sent earlier whose outcome is unknown: only a list read
          // that started after its hold can say the station is not there.
          const address = String(station.decoderAddress ?? '')
          const until = address ? createHoldUntil(interfaceId, address) : undefined
          if (until === undefined) return true
          if (listReadStartedAt() > until) {
            releaseCreateHold(interfaceId, address)
            return true
          }
          held++
          heldUntil = heldUntil === null ? until : Math.min(heldUntil, until)
          return false
        })
        .slice(0, limit)

      if (ready.length === 0 && editedSavedReady.length === 0)
        return {
          created: 0,
          failed: 0,
          updated: 0,
          createdAddresses: [],
          createdRowIds: [],
          updatedIds: [],
          failures: [],
          refused,
          error: null,
          stillOut: writesInFlightRef.current.size,
          held,
          heldUntil,
        }

      onWriteStart?.()

      let failed = 0
      /** `address-index` of each row written, for the caller to retire. */
      const createdAddresses: string[] = []
      /** The temp row ids behind them, whose staged values go with the row. */
      const createdRowIds: string[] = []
      /** Ids of saved stations whose staged edit was applied. */
      const updatedIds: string[] = []
      /** The first thing the server actually said, so a failure is diagnosable. */
      let error: string | null = null
      /** Per row, so the list can show which ones are stuck and why. */
      const failures: { rowId: string; message: string }[] = []
      /**
       * Rows retired without being written, because their decoder already had
       * a station. Counted apart from the writes so the toolbar does not
       * report a create that never happened.
       */
      const skippedRowIds: string[] = []
      const decoderSync = newDecoderPurposeSync()
      /** Created stations whose decoder could not take their Type. */
      const decoderSyncFailed: string[] = []

      /**
       * The decoders that already hold a station, read fresh on every
       * iteration.
       *
       * The API takes the decoder address as the thing a station occupies, so
       * a second station on one address is a record neither of them can be
       * updated through afterwards — it has to not be written in the first
       * place. Two ways it used to happen: a create that timed out client-side
       * but landed on the server was retried on the next pass, and a draft
       * that outlived the refetch which should have retired it was written
       * again. `rowsRef` is live, so a station that arrives mid-batch stops the
       * rows behind it.
       */
      const stationedAddresses = () =>
        new Set([
          ...rowsRef.current
            .filter((row) => Number(row.id) > 0)
            .map((row) => String((row as any).decoderAddress ?? ''))
            .filter((address) => address && address !== '-'),
          ...writtenAddressesRef.current,
        ])

      for (const station of ready) {
        const address = String((station as any).decoderAddress ?? '')

        if (
          address &&
          (createdAddresses.includes(address) || stationedAddresses().has(address))
        ) {
          // Retired, not written: the draft goes away exactly as a saved one
          // does, and the controller keeps the single station it already has.
          createdAddresses.push(address)
          createdRowIds.push(String(station.rowId))
          skippedRowIds.push(String(station.rowId))
          continue
        }

        const sentAt = Date.now()
        try {
          const rowId = String(station.rowId)
          const written = (pendingEdits.get(rowId) ?? {}) as Record<
            string,
            unknown
          >
          // Held before it is sent, and in sessionStorage: if the step is left
          // or the page reloaded while this is out, the draft that comes back
          // is not written again until a list read can tell.
          holdCreateSent(interfaceId, address, sentAt)
          const rowSyncFailed: string[] = []
          await sendTrackedWrite(
            writesInFlightRef.current,
            `create:${address}`,
            Promise.resolve(
              stationDataConfigApi.create(
                buildCreatePayload(station, {
                  interfaceId,
                  controllerOptionsData,
                  plantOptionsData,
                  soilOptionsData,
                  emitterOptionsData,
                  etZoneOptionsData,
                }) as any,
              ),
            ).then(async (value) => {
              // Before the draft is retired: the wizard checks the new station
              // against its decoder as soon as it appears in the list.
              await syncDecoderAfterCreate(decoderSync, station, rowSyncFailed)
              return value
            }),
            t('stationsSaveTimeout'),
            (outcome) => {
              if (outcome.ok) {
                // Landed after all: no pass may create it again, and the
                // list re-read that the caller starts retires the draft.
                writtenAddressesRef.current.add(address)
                onRowSaved?.(rowId, address, written)
                reportDecoderSyncFailures(rowSyncFailed)
              }
              // Refused: the next pass may try again. A 5xx or a network
              // error says nothing — held until a later list read decides.
              settleCreateHold(
                interfaceId,
                address,
                outcome.ok
                  ? 'created'
                  : isUnclearCreateFailure(outcome.error)
                    ? 'unclear'
                    : 'refused',
                sentAt,
                { late: true },
              )
              onLateWriteSettledRef.current?.({
                kind: 'create',
                rowId,
                ok: outcome.ok,
                message: outcome.ok ? undefined : getApiErrorMessage(outcome.error),
              })
            },
          )
          settleCreateHold(interfaceId, address, 'created', sentAt)
          decoderSyncFailed.push(...rowSyncFailed)
          // Deliberately NOT `removePendingEdit` here. Dropping the staged
          // values while the draft row is still drawn empties the row on
          // screen — and it stays drawn for the rest of the pass, up to
          // twenty-five rows and several seconds of them going blank one by
          // one. The caller retires each written draft and clears its buffer
          // together, in one update, so the row leaves rather than empties.
          createdAddresses.push(String((station as any).decoderAddress))
          createdRowIds.push(String(station.rowId))
          onRowSaved?.(
            String(station.rowId),
            address,
            (pendingEdits.get(String(station.rowId)) ?? {}) as Record<
              string,
              unknown
            >,
          )
        } catch (caught: any) {
          // Left staged and reported. A row that cannot be written stays on
          // screen with its values, and the next pass tries it again.
          failed++
          const message = getApiErrorMessage(caught)
          failures.push({ rowId: String(station.rowId), message })
          if (!error) error = message
          // A timeout leaves the write tracked (and the hold in place) until
          // it answers. A 5xx or network error says nothing about what the
          // server did either, so the retry waits for a list read after the
          // settle window; only a 4xx frees the decoder now.
          if (!(caught instanceof WriteTimeoutError)) {
            settleCreateHold(
              interfaceId,
              address,
              isUnclearCreateFailure(caught) ? 'unclear' : 'refused',
              sentAt,
            )
          }
        }
        await new Promise((resolve) => setTimeout(resolve, AUTOSAVE_REQUEST_GAP_MS))
      }

      // Read once per pass, and only when there is an update to send.
      //
      // The update is a full replace and the grid rows carry no pin, so a
      // write without this map sends nulls and wipes every edited station's
      // geotag. If the read fails or hangs, the updates wait for the next pass
      // and stay staged: not attempted, so not counted as failed
      // (SCRUM-1032 round 7 review).
      const geoById = editedSavedReady.length
        ? await withTimeout(
            readStationGeoTags(interfaceId),
            t('stationsSaveTimeout'),
          ).catch((caught) => {
            console.error('Station geotags unavailable; updates wait:', caught)
            return null
          })
        : undefined
      const updatesThisPass = geoById === null ? [] : editedSavedReady

      for (const station of updatesThisPass) {
        try {
          const payload = buildUpdatePayload(station, {
            interfaceId,
            controllerOptionsData,
            plantOptionsData,
            soilOptionsData,
            emitterOptionsData,
            etZoneOptionsData,
            geoById: geoById ?? undefined,
          })
          // A station with no decoder is not a station. If the address could
          // not be read, the row is left alone rather than written back as
          // "0-0" — losing the decoder is worse than not saving the edit.
          if (!payload.address) {
            failed++
            const message = t('stationsSaveDecoderReadFailed')
            failures.push({ rowId: String(station.rowId), message })
            if (!error) error = message
            continue
          }
          const rowId = String(station.rowId)
          const sentEdit = (pendingEdits.get(rowId) ?? {}) as Partial<StationRow>
          /**
           * The base row still holds the value this edit replaced, and an
           * update-only pass never refetches — so dropping the staged edit on
           * its own snapped the cell back to the old value, which reads as the
           * save having wiped what was typed. The row takes the saved values
           * first; only then is the edit no longer pending. Only what was
           * sent is dropped, and only where it still holds the value sent:
           * anything typed while the request was out stays (round 7, item 2).
           */
          let savedPurpose: string | null = null
          const retireSent = () => {
            // Only the fields this write sent. The rest of the send-time
            // snapshot may be older than the row now: a refetch may have
            // normalised values or moved state/errorCode since, and laying
            // the snapshot back over the row undid that (SCRUM-1032 review).
            onSaved([
              {
                id: (station as any).id,
                ...sentEdit,
                ...(savedPurpose ? { decoderType: savedPurpose } : {}),
              } as StationRow,
            ])
            apiRef.current?.removePendingEdit(
              rowId,
              Object.keys(sentEdit),
              sentEdit,
            )
            onRowSaved?.(rowId, String((station as any).decoderAddress ?? ''))
          }

          // SCRUM-1145 — the decoder first, and put back if the station is
          // refused, as in `handleSave`.
          const writeStation = () => Promise.resolve(editStationAsync(payload))
          const request = Promise.resolve().then(async () => {
            if (!needsDecoderPurposeSync(station)) {
              await writeStation()
              return
            }
            savedPurpose = await decoderSync.withStation(
              station,
              writeStation,
              isServerRefusal,
            )
            if (savedPurpose) (station as any).decoderType = savedPurpose
          })

          await sendTrackedWrite(
            writesInFlightRef.current,
            `update:${rowId}`,
            request,
            t('stationsSaveTimeout'),
            (outcome) => {
              if (outcome.ok) retireSent()
              onLateWriteSettledRef.current?.({
                kind: 'update',
                rowId,
                ok: outcome.ok,
                message: outcome.ok ? undefined : getApiErrorMessage(outcome.error),
              })
            },
          )

          retireSent()
          updatedIds.push(rowId)
        } catch (caught: any) {
          failed++
          const message = getApiErrorMessage(caught)
          failures.push({ rowId: String(station.rowId), message })
          if (!error) error = message
        }
        await new Promise((resolve) => setTimeout(resolve, AUTOSAVE_REQUEST_GAP_MS))
      }

      // Re-read the list when something went wrong, and only then. A timeout
      // is a client-side giving-up, not a cancellation: the server may yet have
      // created the station, and reading the list is how the next pass finds
      // out instead of writing it twice.
      //
      // A clean pass needs no re-read. The caller already knows every address
      // it just wrote (`onRowSaved`), which is what retires those drafts — and
      // on a long list this ran between every batch, refetching hundreds of
      // rows and re-rendering the grid each time, for an answer already in
      // hand. The caller refreshes once when the queue drains.
      if (failed > 0) onNewStationsCreated?.()
      reportDecoderSyncFailures(decoderSyncFailed)

      return {
        created: createdAddresses.length - skippedRowIds.length,
        failed,
        updated: updatedIds.length,
        createdAddresses,
        createdRowIds,
        updatedIds,
        failures,
        refused,
        error,
        stillOut: writesInFlightRef.current.size,
        held,
        heldUntil,
      }
    },
    [
      apiRef,
      rowsRef,
      interfaceId,
      controllerOptionsData,
      plantOptionsData,
      soilOptionsData,
      emitterOptionsData,
      etZoneOptionsData,
      onNewStationsCreated,
      editStationAsync,
      newDecoderPurposeSync,
      onSaved,
      onRowSaved,
      reportDecoderSyncFailures,
      syncDecoderAfterCreate,
      writtenAddressesRef,
      t,
      isWriteReady,
      listReadStartedAt,
      writesInFlightRef,
    ],
  )

  return { handleSave, handleSaveRow, saveCompleteRows }
}
